import { useState, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
    Calculator,
    DollarSign,
    Percent,
    Target,
    TrendingDown,
    AlertTriangle,
    AlertCircle,
    CheckCircle2,
    Info,
    RefreshCw
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';

// Instrument configurations with pip values
const instruments = {
    XAUUSD: {
        name: 'XAU/USD',
        fullName: 'Gold',
        pipValue: 0.1, // Per 0.01 lot, pip = $0.01 move = $0.10
        pipSize: 0.01, // 1 pip = 0.01 price movement
        contractSize: 100, // 100 oz per lot
        category: 'commodity'
    },
    EURUSD: {
        name: 'EUR/USD',
        fullName: 'Euro / US Dollar',
        pipValue: 10, // Per 1 lot, 1 pip = $10
        pipSize: 0.0001,
        contractSize: 100000,
        category: 'forex'
    },
    GBPUSD: {
        name: 'GBP/USD',
        fullName: 'British Pound / US Dollar',
        pipValue: 10,
        pipSize: 0.0001,
        contractSize: 100000,
        category: 'forex'
    },
    USDJPY: {
        name: 'USD/JPY',
        fullName: 'US Dollar / Japanese Yen',
        pipValue: 6.5, // Approximate at ~150 rate
        pipSize: 0.01,
        contractSize: 100000,
        category: 'forex'
    },
    GBPJPY: {
        name: 'GBP/JPY',
        fullName: 'British Pound / Japanese Yen',
        pipValue: 6.5,
        pipSize: 0.01,
        contractSize: 100000,
        category: 'forex'
    },
    AUDUSD: {
        name: 'AUD/USD',
        fullName: 'Australian Dollar / US Dollar',
        pipValue: 10,
        pipSize: 0.0001,
        contractSize: 100000,
        category: 'forex'
    },
    USDCAD: {
        name: 'USD/CAD',
        fullName: 'US Dollar / Canadian Dollar',
        pipValue: 7.5, // Approximate
        pipSize: 0.0001,
        contractSize: 100000,
        category: 'forex'
    },
    BTCUSD: {
        name: 'BTC/USD',
        fullName: 'Bitcoin',
        pipValue: 1, // Per 1 contract, $1 move = $1
        pipSize: 1,
        contractSize: 1,
        category: 'crypto'
    }
};

const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' }
];

// Exchange rates to USD (approximate)
const exchangeRates = {
    USD: 1,
    MYR: 0.21,
    SGD: 0.74,
    EUR: 1.08,
    GBP: 1.27
};

export const RiskCalculator = ({ className }) => {
    const { language } = useLanguage();
    
    const [formData, setFormData] = useState({
        accountBalance: '',
        currency: 'USD',
        riskPercent: '',
        stopLossPips: '',
        instrument: 'XAUUSD'
    });

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleReset = () => {
        setFormData({
            accountBalance: '',
            currency: 'USD',
            riskPercent: '',
            stopLossPips: '',
            instrument: 'XAUUSD'
        });
    };

    // Calculate results
    const results = useMemo(() => {
        const balance = parseFloat(formData.accountBalance) || 0;
        const riskPct = parseFloat(formData.riskPercent) || 0;
        const stopLoss = parseFloat(formData.stopLossPips) || 0;
        const instrument = instruments[formData.instrument];
        const exchangeRate = exchangeRates[formData.currency] || 1;

        // Convert balance to USD
        const balanceUSD = balance * exchangeRate;

        // Calculate risk amount in USD
        const riskAmountUSD = balanceUSD * (riskPct / 100);

        // Calculate lot size based on instrument
        let lotSize = 0;
        if (stopLoss > 0 && instrument) {
            // For XAUUSD: lotSize = riskAmount / (stopLoss * 10)
            // For Forex pairs: lotSize = riskAmount / (stopLoss * pipValue)
            if (formData.instrument === 'XAUUSD') {
                // For gold: 1 pip = $0.10 per 0.01 lot, so 1 pip = $10 per 1.0 lot
                lotSize = riskAmountUSD / (stopLoss * 10);
            } else if (formData.instrument === 'BTCUSD') {
                // For BTC: Each $1 move per 1 contract = $1
                lotSize = riskAmountUSD / stopLoss;
            } else {
                // For standard forex pairs
                lotSize = riskAmountUSD / (stopLoss * instrument.pipValue);
            }
        }

        // Round lot size to appropriate precision
        lotSize = Math.floor(lotSize * 100) / 100; // Round down to 0.01

        // Convert risk amount back to original currency
        const riskAmountLocal = riskAmountUSD / exchangeRate;

        // Max loss is the same as risk amount
        const maxLoss = riskAmountLocal;

        return {
            riskAmount: riskAmountLocal,
            riskAmountUSD,
            lotSize,
            maxLoss,
            isValid: balance > 0 && riskPct > 0 && stopLoss > 0
        };
    }, [formData]);

    // Derive warnings from form data and results (no useEffect needed)
    const warnings = useMemo(() => {
        const riskPct = parseFloat(formData.riskPercent) || 0;
        const stopLoss = parseFloat(formData.stopLossPips) || 0;

        return {
            highRisk: riskPct > 2,
            lowStopLoss: stopLoss > 0 && stopLoss < 5 && formData.instrument !== 'BTCUSD',
            highLotSize: results.lotSize > 1
        };
    }, [formData.riskPercent, formData.stopLossPips, formData.instrument, results.lotSize]);

    const currencySymbol = currencies.find(c => c.code === formData.currency)?.symbol || '$';
    const selectedInstrument = instruments[formData.instrument];

    return (
        <div className={cn("w-full max-w-2xl mx-auto", className)} data-testid="risk-calculator">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
                    <Calculator className="w-5 h-5 text-primary" />
                    <span className="text-sm font-semibold text-primary">Risk Calculator</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
                    {language === 'id' ? 'Kalkulator Risiko' : 'Risk Calculator'}
                </h2>
                <p className="text-slate-500 mt-2">
                    {language === 'id' 
                        ? 'Hitung lot size berdasarkan pengurusan risiko'
                        : 'Calculate lot size based on risk management'
                    }
                </p>
            </div>

            {/* Calculator Card */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                {/* Input Section */}
                <div className="p-6 space-y-5">
                    {/* Row 1: Account Balance & Currency */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                                <DollarSign className="w-4 h-4 text-slate-400" />
                                {language === 'id' ? 'Baki Akaun' : 'Account Balance'}
                            </label>
                            <input
                                type="number"
                                value={formData.accountBalance}
                                onChange={(e) => handleInputChange('accountBalance', e.target.value)}
                                placeholder="10000"
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                data-testid="input-balance"
                            />
                        </div>
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                                {language === 'id' ? 'Mata Wang' : 'Currency'}
                            </label>
                            <select
                                value={formData.currency}
                                onChange={(e) => handleInputChange('currency', e.target.value)}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-white"
                                data-testid="select-currency"
                            >
                                {currencies.map(currency => (
                                    <option key={currency.code} value={currency.code}>
                                        {currency.symbol} {currency.code}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Row 2: Risk Percent & Instrument */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                                <Percent className="w-4 h-4 text-slate-400" />
                                {language === 'id' ? 'Risiko Per Trade (%)' : 'Risk Per Trade (%)'}
                            </label>
                            <input
                                type="number"
                                step="0.1"
                                value={formData.riskPercent}
                                onChange={(e) => handleInputChange('riskPercent', e.target.value)}
                                placeholder="1"
                                className={cn(
                                    "w-full px-4 py-3 border rounded-xl text-lg focus:outline-none focus:ring-2 transition-all",
                                    warnings.highRisk 
                                        ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
                                        : "border-slate-200 focus:ring-primary/20 focus:border-primary"
                                )}
                                data-testid="input-risk"
                            />
                            {warnings.highRisk && (
                                <p className="text-xs text-rose-600 mt-1 flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    {language === 'id' ? 'Risk > 2% adalah tinggi' : 'Risk > 2% is high'}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                                <TrendingDown className="w-4 h-4 text-slate-400" />
                                Instrument / Pair
                            </label>
                            <select
                                value={formData.instrument}
                                onChange={(e) => handleInputChange('instrument', e.target.value)}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-white"
                                data-testid="select-instrument"
                            >
                                {Object.entries(instruments).map(([key, inst]) => (
                                    <option key={key} value={key}>
                                        {inst.name} ({inst.fullName})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Row 3: Stop Loss */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                            <Target className="w-4 h-4 text-slate-400" />
                            Stop Loss ({formData.instrument === 'BTCUSD' ? 'USD' : 'Pips'})
                        </label>
                        <input
                            type="number"
                            value={formData.stopLossPips}
                            onChange={(e) => handleInputChange('stopLossPips', e.target.value)}
                            placeholder={formData.instrument === 'XAUUSD' ? "50" : formData.instrument === 'BTCUSD' ? "500" : "20"}
                            className={cn(
                                "w-full px-4 py-3 border rounded-xl text-lg focus:outline-none focus:ring-2 transition-all",
                                warnings.lowStopLoss 
                                    ? "border-amber-300 focus:ring-amber-200 focus:border-amber-400"
                                    : "border-slate-200 focus:ring-primary/20 focus:border-primary"
                            )}
                            data-testid="input-stoploss"
                        />
                        {warnings.lowStopLoss && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                <Info className="w-3 h-3" />
                                {language === 'id' 
                                    ? 'SL terlalu kecil boleh menyebabkan over-risk'
                                    : 'Small SL may cause over-risk'
                                }
                            </p>
                        )}
                    </div>
                </div>

                {/* Results Section */}
                <div className="bg-slate-50 border-t border-slate-100 p-6">
                    {results.isValid ? (
                        <div className="space-y-4">
                            {/* Warning Banner */}
                            {warnings.highRisk && (
                                <div className="flex items-start gap-3 p-4 bg-rose-50 border border-rose-200 rounded-xl" data-testid="warning-high-risk">
                                    <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-rose-800">
                                            {language === 'id' 
                                                ? 'Risk terlalu besar!'
                                                : 'Risk too high!'
                                            }
                                        </p>
                                        <p className="text-sm text-rose-700 mt-1">
                                            {language === 'id'
                                                ? 'Kurangi risiko per trade untuk melindungi modal.'
                                                : 'Reduce risk per trade to protect your capital.'
                                            }
                                        </p>
                                    </div>
                                </div>
                            )}

                            {warnings.highLotSize && !warnings.highRisk && (
                                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="warning-high-lot">
                                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-amber-800">
                                            {language === 'id'
                                                ? 'Lot size besar'
                                                : 'Large lot size'
                                            }
                                        </p>
                                        <p className="text-sm text-amber-700 mt-1">
                                            {language === 'id'
                                                ? 'Pastikan sesuai dengan trading plan anda.'
                                                : 'Make sure it fits your trading plan.'
                                            }
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Results Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Risk Amount */}
                                <div className="bg-white rounded-xl p-4 border border-slate-200" data-testid="result-risk-amount">
                                    <p className="text-sm text-slate-500 mb-1">
                                        {language === 'id' ? 'Jumlah Risiko' : 'Risk Amount'}
                                    </p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {currencySymbol}{results.riskAmount.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                        ≈ ${results.riskAmountUSD.toFixed(2)} USD
                                    </p>
                                </div>

                                {/* Lot Size */}
                                <div className={cn(
                                    "rounded-xl p-4 border",
                                    warnings.highLotSize 
                                        ? "bg-amber-50 border-amber-200"
                                        : "bg-emerald-50 border-emerald-200"
                                )} data-testid="result-lot-size">
                                    <p className="text-sm text-slate-500 mb-1">
                                        {language === 'id' ? 'Saiz Lot Disyorkan' : 'Recommended Lot Size'}
                                    </p>
                                    <p className={cn(
                                        "text-3xl font-bold",
                                        warnings.highLotSize ? "text-amber-700" : "text-emerald-700"
                                    )}>
                                        {results.lotSize.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-1">
                                        {selectedInstrument?.name}
                                    </p>
                                </div>

                                {/* Max Loss */}
                                <div className="bg-white rounded-xl p-4 border border-slate-200" data-testid="result-max-loss">
                                    <p className="text-sm text-slate-500 mb-1">
                                        {language === 'id' ? 'Kerugian Maksimum' : 'Max Loss'}
                                    </p>
                                    <p className="text-2xl font-bold text-rose-600">
                                        {currencySymbol}{results.maxLoss.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                        {language === 'id' ? 'Jika SL kena' : 'If SL hits'}
                                    </p>
                                </div>
                            </div>

                            {/* Success Indicator */}
                            {!warnings.highRisk && !warnings.highLotSize && (
                                <div className="flex items-center justify-center gap-2 py-3 text-emerald-600" data-testid="success-indicator">
                                    <CheckCircle2 className="w-5 h-5" />
                                    <span className="text-sm font-medium">
                                        {language === 'id' 
                                            ? 'Risk dalam had selamat'
                                            : 'Risk within safe limits'
                                        }
                                    </span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500">
                                {language === 'id' 
                                    ? 'Masukkan semua nilai untuk melihat hasil'
                                    : 'Enter all values to see results'
                                }
                            </p>
                        </div>
                    )}
                </div>

                {/* Reset Button */}
                <div className="px-6 pb-6 pt-2 flex justify-center">
                    <Button
                        variant="outline"
                        onClick={handleReset}
                        className="rounded-xl"
                        data-testid="reset-calculator"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reset
                    </Button>
                </div>
            </div>

            {/* Reminder Text */}
            <div className="mt-8 text-center">
                <div className="inline-block px-6 py-4 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="text-sm text-slate-600 font-medium">
                        {language === 'id' 
                            ? '"Lot size ikut risk, bukan ikut emosi.'
                            : '"Lot size follows risk, not emotion.'
                        }
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                        {language === 'id'
                            ? 'Trader bertahan lama kerana disiplin risiko."'
                            : 'Traders survive long due to risk discipline."'
                        }
                    </p>
                </div>
            </div>

            {/* Info Tooltip */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">
                            {language === 'id' ? 'Nota Pengiraan:' : 'Calculation Notes:'}
                        </p>
                        <ul className="text-blue-700 space-y-1 text-xs">
                            <li>• {language === 'id' 
                                ? 'Nilai pip berbeza mengikut instrument'
                                : 'Pip value varies by instrument'
                            }</li>
                            <li>• {language === 'id'
                                ? 'Untuk XAUUSD: 1 pip = $0.10 per 0.01 lot'
                                : 'For XAUUSD: 1 pip = $0.10 per 0.01 lot'
                            }</li>
                            <li>• {language === 'id'
                                ? 'Untuk Forex: 1 pip = $10 per 1.0 lot (pairs USD)'
                                : 'For Forex: 1 pip = $10 per 1.0 lot (USD pairs)'
                            }</li>
                            <li>• {language === 'id'
                                ? 'Sentiasa semak dengan broker anda'
                                : 'Always verify with your broker'
                            }</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};
